import { memo } from "react";
import { IconType } from "../../../common/components/icon/IconType";
import { IconSize } from "../../../common/components/icon/IconSize";
import { Icon } from "../../../common/components/icon/Icon";
import { Button } from "../../../common/components/Button/Button";
import { Link } from "../../../common/components/Link";
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material";
import Grid from "@mui/material/Grid";
import { HousingSATileSelect, TileSelectVariant } from "../Inputs/TileSelect/TileSelect";
import { HousingSACheckboxSelect } from "../Inputs/CheckboxSelect/CheckboxSelect";
import { SASection, SASectionContents, SASectionDescription, SASectionTitle } from "../Section/SASection";
import { SingleDimensionInput } from "../Inputs/DualUnitNumberInput/SingleDimensionInput";
import { RHFDualSystemNumberInput } from "../Inputs/DualUnitNumberInput/DualSystemNumberInput";
import { useFormContext } from "react-hook-form";
import { type HousingSearchForm } from "../../types/housingSAFormTypes";
import { TemperatureSwitch, UnitSwitch } from "./Switches";
import { selectDimensionTileConfig } from "../../redux/selectors";
import { useSelector } from "react-redux";
import { HousingSAAutocompleteInput } from "../Inputs/HSAAutosuggestInput";
import { type HousingSearchFormKey, useHSAResetAction } from "../../HousingSelectionAssistant";
import { trackTabsChangedHSA } from "~/feature/Analytics/ga/ga";
import { useHSAFacetData } from "~/HousingSelectionAssistant/components/useHSAFacetData";
import { useTranslation } from "react-i18next";
import { NumberSystem } from "~/HousingSelectionAssistant/types/types";

interface Props {
  resetData: () => void;
}

const ResetLink = ({
  fieldsToReset,
  manualReset = false,
}: {
  fieldsToReset: HousingSearchFormKey[];
  manualReset?: boolean;
}) => {
  const { t } = useTranslation();

  const resetData = useHSAResetAction();

  return (
    <Link
      data-test-id={"housing-assistant-reset"}
      className={"mt-auto"}
      onClick={() => resetData(manualReset, ...fieldsToReset)}
    >
      <Icon type={IconType.Reset} size={IconSize.FONT_20} className="icon-margin icon-flipped" />
      {t("page.linear.links.reset")}
    </Link>
  );
};

export const MobileHousingFilters = memo((props: Props) => {
  return <HousingFilters {...props} />;
});

const HousingFilters = ({ resetData }: Props) => {
  const { t } = useTranslation();
  const formProps = useFormContext<HousingSearchForm>();
  const unit = formProps.watch("dimensionUnit");
  const housingTypeState = formProps.watch("housing_type");
  const housingInnerState = formProps.watch("housingdinner");
  const housingOuterState = formProps.watch("housingdouter");
  const searchDesignationState = formProps.watch("housinginclbrg");
  const dimensionsConfig = useSelector(selectDimensionTileConfig);
  const { values: valuesHousingType } = useHSAFacetData({ name: "housing_type" });

  const resetLink = <ResetLink fieldsToReset={["housinginclbrg", "bearing_type", "bearing_design"]} />;

  const renderGaEvent = (
    pushContext:
      | "item_from_designation_search_dropdown_selected"
      | "shaft_diameter_provided"
      | "outside_diameter_provided",
    searchDesignation?: string,
  ) => {
    const selectedHousingType =
      !!valuesHousingType && !!dimensionsConfig
        ? valuesHousingType.filter((item) => item.code === housingTypeState)[0]
        : undefined;
    const selectedHousingTypeLabel =
      !!selectedHousingType && selectedHousingType.label ? selectedHousingType.label : undefined;

    trackTabsChangedHSA(
      "Bearing & Design Envelope",
      pushContext,
      housingInnerState,
      housingOuterState,
      selectedHousingTypeLabel,
      searchDesignation ? searchDesignation : searchDesignationState,
    );
  };

  const submitFuncHelperGA = () => {
    if (housingInnerState.min !== "" || housingInnerState.max !== "") {
      renderGaEvent("shaft_diameter_provided");
    }
    if (housingOuterState.min !== "" || housingOuterState.max !== "") {
      renderGaEvent("outside_diameter_provided");
    }
  };

  const submitButton = (
    <Button data-test-id={"housing-assistant-apply"} type={"submit"} onClick={() => submitFuncHelperGA()}>
      {t("searchPage.refinement.button.apply")}
    </Button>
  );

  const applyAndReset = (
    <div className="mt-auto d-flex flex-align-center flex-justify-between">
      {resetLink}
      {submitButton}
    </div>
  );

  const designationSearch = (
    <div className={"scha__housing-selection__designation-search-mobile"}>
      <HousingSAAutocompleteInput
        name={"housinginclbrg"}
        renderGA={(searchDesignation) =>
          renderGaEvent("item_from_designation_search_dropdown_selected", searchDesignation)
        }
      />
    </div>
  );

  const bearingCharacteristics = (
    <Grid container className={"mt-60"}>
      <Grid
        size={{
          phone: 12
        }}>
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_type"} multiple={true} />
      </Grid>
      <Grid
        size={{
          phone: 12
        }}>
        <HousingSACheckboxSelect className={"mb-30"} name={"bearing_design"} multiple={true} />
      </Grid>
    </Grid>
  );

  const housingCharacteristics = (
    <>
      <HousingSATileSelect className={"mb-30 pt-30 scha__housing-selection__type-mobile"} name={"housing_type"} />

      {!!formProps.watch("housing_type") && (
        <>
          <HousingSATileSelect className={"mb-30"} name={"number_of_bolts"} multiple={true} />

          <HousingSATileSelect
            className={"mb-30 scha__housing-selection__type-mobile"}
            name={"housing_shapes"}
            multiple={true}
          />
        </>
      )}

      <Grid container className={"mt-1 pt-30"}>
        <Grid
          size={{
            phone: 6
          }}>
          <HousingSACheckboxSelect className={"mb-30"} name={"housing_design"} multiple={true} />
        </Grid>
      </Grid>
    </>
  );

  return (
    <div>
      <div id="tabs">
        <Accordion>
          <AccordionSummary>{t("page.housingSelectionAssistant.bearing&design")}</AccordionSummary>
          <AccordionDetails className={"scha__accordion-content-mobile"}>
            <SASection className={"scha__housing-selection__section--bearing&sealing"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
              </SASectionDescription>
              <SASectionContents>
                {designationSearch}
                <div className={"mt-1 pt-2"}>
                  <Button
                    href="#dimensions-up"
                    data-test-id={"btn-bore-diameter"}
                    className={"button scha__btn-find-assistant"}
                    data-ta-spec={"housing_assistant_diameter_scroller"}
                    size={"medium"}
                    startIcon={<Icon type={IconType.ArrowDown} size={IconSize.FONT_24} />}
                  >
                    {t("page.housingselectionassistant.btn.enterBoreDia")}
                  </Button>
                </div>
                {bearingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--design"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.designEnvelope")}</SASectionTitle>
              <SASectionDescription>{t("page.housingSelectionAssistant.info.housingType")}</SASectionDescription>

              <SASectionContents>
                {housingCharacteristics}
                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--dimensions"} id="dimensions-up">
              <SASectionTitle>{t("page.selectionAssistant.tab.dimensions")}</SASectionTitle>

              <SASectionContents>
                <UnitSwitch className={"mb-60"} />
                <div className="scha__housing-selection__dimensions-grid">
                  {dimensionsConfig.map((techFilter) => (
                    <div key={techFilter.code} className="scha__housing-selection__dimensions-grid__item">
                      <SingleDimensionInput label={techFilter.label} mode={unit} name={techFilter.code} mobile={true} />
                    </div>
                  ))}
                </div>

                {applyAndReset}
              </SASectionContents>
            </SASection>
          </AccordionDetails>
        </Accordion>
        <Accordion>
          <AccordionSummary>{t("page.housingSelectionAssistant.systemCharacteristics")}</AccordionSummary>
          <AccordionDetails className={"scha__accordion-content-mobile"}>
            <SASection className={"scha__housing-selection__section--system"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.system.name")}</SASectionTitle>
              <SASectionDescription>{t("page.housingselectionassistant.cluster.system.subtitle")}</SASectionDescription>
              <SASectionContents>
                <Grid container className={"scha__housing-selection__bearingsealing-grid"}>
                  <Grid>
                    <HousingSATileSelect name={"housingload"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect
                      name={"housinglubrication"}
                      variant={TileSelectVariant.ICN_LEFT}
                      multiple={true}
                    />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect name={"housingcoating"} variant={TileSelectVariant.ICN_LEFT} multiple={true} />
                  </Grid>

                  <Grid>
                    <HousingSATileSelect
                      name={"housingmisalignment"}
                      variant={TileSelectVariant.ICN_LEFT}
                      multiple={true}
                    />
                  </Grid>
                </Grid>

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--bearing&sealing"}>
              <SASectionTitle>{t("page.housingSelectionAssistant.bearingSealing")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingSelectionAssistant.info.bearingSealingCharacteristics")}
              </SASectionDescription>

              <SASectionContents>
                {designationSearch}

                {bearingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--housing"}>
              <SASectionTitle>
                {t("page.housingselectionassistant.cluster.housing_characteristics.name")}
              </SASectionTitle>
              <SASectionDescription>
                {t("page.housingselectionassistant.cluster.housing_characteristics.subtitle")}
              </SASectionDescription>

              <SASectionContents>
                <HousingSAAutocompleteInput name={"housinginclhsg"} />

                {housingCharacteristics}

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--temperature"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.temperature_range.name")}</SASectionTitle>
              <SASectionDescription>
                {t("page.housingselectionassistant.cluster.temperature_range.subtitle")}
              </SASectionDescription>

              <SASectionContents>
                <TemperatureSwitch className={"mb-30"} />

                <div className={"scha__housing-selection__dimensions-grid"}>
                  <div>
                    <RHFDualSystemNumberInput
                      mode={NumberSystem.Metric}
                      data-test-id={"housing-sa-temp-min"}
                      label={t("searchPage.refinement.value.label.min")}
                      name={"housingtempmin.min"}
                    />
                  </div>

                  <div>
                    <RHFDualSystemNumberInput
                      mode={NumberSystem.Metric}
                      data-test-id={"housing-sa-temp-max"}
                      label={t("searchPage.refinement.value.label.max")}
                      name={"housingtempmin.max"}
                    />
                  </div>
                </div>

                {applyAndReset}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--bearing-mounting"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.bearing_mounting_type.name")}</SASectionTitle>

              <SASectionContents>
                <HousingSACheckboxSelect
                  className={"mb-30"}
                  omitLabel={true}
                  name={"bearing_mounting_type"}
                  columnAmount={2}
                  multiple={true}
                />

                {resetLink}
              </SASectionContents>
            </SASection>

            <SASection className={"scha__housing-selection__section--housing-material"}>
              <SASectionTitle>{t("page.housingselectionassistant.cluster.housing_material.name")}</SASectionTitle>

              <SASectionContents>
                <HousingSACheckboxSelect
                  className={"mb-30"}
                  omitLabel={true}
                  name={"housing_material"}
                  columnAmount={2}
                  multiple={true}
                />

                {resetLink}
              </SASectionContents>
            </SASection>
          </AccordionDetails>
        </Accordion>
      </div>
    </div>
  );
};
